import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { MenuItemLink } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  GetSubmittedRfiCountDocument,
  RequestForInvoicingStatusEnum,
  useDeleteRequestForInvoicingMutation,
  useSubmitRequestForInvoicingMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can, withCan } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { PageRequestsForInvoicingCtx } from "../PageRequestsForInvoicing";
import type { TData } from "./TableRequestsForInvoices";

interface PopoverRowActionsProps {
  item: TData;
}

export const PopoverRowActions = withCan(
  {
    do: [
      "requestForInvoicing_update_all",
      "requestForInvoicing_update_own",
      "requestForInvoicing_update_team",
      "requestForInvoicing_delete_all",
      "requestForInvoicing_delete_own",
      "requestForInvoicing_delete_team",
    ],
  },
  observer(function PopoverRowActions({ item }: PopoverRowActionsProps) {
    const { id, status, ownerId } = item;
    const store = useContext(PageRequestsForInvoicingCtx);

    const deleteRFIDialog = useDisclosure();

    const [deleteRFI] = useDeleteRequestForInvoicingMutation({
      variables: { id },
      ...onError(),
      onCompleted() {
        store.refetch?.();
      },
      refetchQueries: [{ query: GetSubmittedRfiCountDocument }],
    });

    const [submitRFI] = useSubmitRequestForInvoicingMutation({
      variables: { id },
      ...onError(),
      onCompleted() {
        store.refetch?.();
      },
      refetchQueries: [{ query: GetSubmittedRfiCountDocument }],
    });

    const PERMISSION_CAN_REQUEST_FOR_INVOICING_UPDATE =
      can(
        "requestForInvoicing_update_all",
        "requestForInvoicing_update_team",
      ) ||
      can({
        do: "requestForInvoicing_update_own",
        eval: ({ id }) => ownerId === id,
      });

    const PERMISSION_CAN_REQUEST_FOR_INVOICING_ACCOUNTING_ATTRIBUTES =
      can("requestForInvoicing_accessAccountingAttributes") &&
      status === RequestForInvoicingStatusEnum.Submitted;

    const PERMISSION_CAN_REQUEST_FOR_INVOICING_DELETE =
      can("requestForInvoicing_delete_all") ||
      can("requestForInvoicing_delete_team") ||
      can({
        do: "requestForInvoicing_delete_own",
        eval: ({ id }) => ownerId === id,
      });

    if (
      !PERMISSION_CAN_REQUEST_FOR_INVOICING_UPDATE &&
      !PERMISSION_CAN_REQUEST_FOR_INVOICING_ACCOUNTING_ATTRIBUTES &&
      !PERMISSION_CAN_REQUEST_FOR_INVOICING_DELETE
    ) {
      return null;
    }

    return (
      <React.Fragment>
        <Menu>
          <MenuButton>
            <Icon name="dots-horizontal" color="#02929d" />
          </MenuButton>

          <MenuList>
            {PERMISSION_CAN_REQUEST_FOR_INVOICING_UPDATE && (
              <MenuItemLink
                href={{
                  pathname: "/invoices/for-invoicing/edit",
                  query: { id },
                }}
              >
                {can("requestForInvoicing_accessAccountingAttributes")
                  ? t`Check`
                  : t`Edit`}
              </MenuItemLink>
            )}
            {can(
              "requestForInvoicing_create_all",
              "requestForInvoicing_create_own",
              "requestForInvoicing_create_team",
            ) && (
              <MenuItemLink
                href={{
                  pathname: "/invoices/for-invoicing/duplicate",
                  query: { id },
                }}
              >
                <Trans>Duplicate</Trans>
              </MenuItemLink>
            )}
            {PERMISSION_CAN_REQUEST_FOR_INVOICING_UPDATE &&
              status === RequestForInvoicingStatusEnum.Draft && (
                <MenuItem onClick={() => submitRFI()}>
                  <Trans>Submit</Trans>
                </MenuItem>
              )}
            {PERMISSION_CAN_REQUEST_FOR_INVOICING_DELETE && (
              <MenuItem onClick={deleteRFIDialog.onOpen}>
                <Trans>Delete</Trans>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
        <ModalConfirm {...deleteRFIDialog} onConfirm={deleteRFI} />
      </React.Fragment>
    );
  }),
);
